import { useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { settingsApi, type ZettlePreview } from '../../services/settings';

function statusLabel(status: ZettlePreview['items'][number]['status']): string {
  if (status === 'matched') return 'Matchad';
  if (status === 'missing_sku') return 'Saknar SKU';
  return 'Ny';
}

function statusClass(status: ZettlePreview['items'][number]['status']): string {
  if (status === 'matched') return 'text-green-700 bg-green-50';
  if (status === 'missing_sku') return 'text-amber-800 bg-amber-50';
  return 'text-blue-700 bg-blue-50';
}

function formatPrice(price: string, currencyId: string | null): string {
  if (price === '') {
    return '-';
  }
  const normalized = Number(price);
  if (!Number.isFinite(normalized)) {
    return currencyId ? `${price} ${currencyId}` : price;
  }
  return `${normalized.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currencyId ? ` ${currencyId}` : ''}`;
}

function matchLabel(item: ZettlePreview['items'][number]): string {
  if (!item.matchedProductId) {
    return '-';
  }
  const method = item.matchMethod === 'name' ? 'namn' : 'SKU';
  return `${item.matchedProductSku ?? '-'} - ${item.matchedProductName ?? ''} (${method})`;
}

export function ZettleSettingsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ZettlePreview | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await settingsApi.getZettlePreview();
        if (active) {
          setPreview(data);
        }
      } catch (error) {
        if (active) {
          toast.error(error instanceof Error ? error.message : 'Kunde inte hämta Zettle-preview');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [toast]);

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Zettle</h2>
      <p className="text-sm text-gray-600">Förhandsvisning av artiklar från Zettle innan någon import byggs. Namn, pris och bild visas direkt från Zettle.</p>

      {loading && !preview ? <p className="text-sm text-gray-500">Laddar preview...</p> : null}

      {preview ? (
        <>
          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="text-sm"><span className="text-gray-500">Client ID:</span> {preview.clientId}</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Zettle</div>
                <div className="text-lg font-semibold">{preview.totals.zettleProducts}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Matchade</div>
                <div className="text-lg font-semibold">{preview.totals.matched}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Nya</div>
                <div className="text-lg font-semibold">{preview.totals.new}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Saknar SKU</div>
                <div className="text-lg font-semibold">{preview.totals.missingSku}</div>
              </div>
            </div>
          </div>

          <div className="rounded border border-gray-200 overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 18rem)' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">Bild</th>
                    <th className="px-2 py-1.5 text-left">Namn</th>
                    <th className="px-2 py-1.5 text-left">Pris</th>
                    <th className="px-2 py-1.5 text-left">SKU</th>
                    <th className="px-2 py-1.5 text-left">Streckkod</th>
                    <th className="px-2 py-1.5 text-left">Moms</th>
                    <th className="px-2 py-1.5 text-left">Kategori</th>
                    <th className="px-2 py-1.5 text-left">Match i ApisAI</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => (
                    <tr key={item.zettleVariantUuid || item.zettleProductUuid} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="h-10 w-10 rounded border border-gray-200 object-cover bg-white"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="text-xs text-gray-500">Nej</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">{item.name}</td>
                      <td className="px-2 py-1.5">{formatPrice(item.price, item.currencyId)}</td>
                      <td className="px-2 py-1.5">{item.sku ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.barcode ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.vatPercentage !== '' ? `${item.vatPercentage}%` : '-'}</td>
                      <td className="px-2 py-1.5">{item.categoryName ?? '-'}</td>
                      <td className="px-2 py-1.5">{matchLabel(item)}</td>
                    </tr>
                  ))}
                  {preview.items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-2 py-4 text-sm text-gray-500">Inga Zettle-artiklar hittades.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
